import React, { useState, useEffect } from 'react';
import { TransaksiPembayaran } from '../../types/index.js';
import { api } from '../../services/api.js';
import { useDialog } from '../ui/DialogProvider.js';
import {
  QrCode,
  CheckCircle2,
  Clock,
  Printer,
  RefreshCw,
  X,
  Smartphone,
  ShieldCheck,
  Building2,
  Sparkles,
  ExternalLink,
} from 'lucide-react';

interface QrisPaymentModalProps {
  transaction: TransaksiPembayaran;
  onClose: () => void;
  onPaymentSuccess: (updatedTrx: TransaksiPembayaran) => void;
}

export const QrisPaymentModal: React.FC<QrisPaymentModalProps> = ({
  transaction: initialTrx,
  onClose,
  onPaymentSuccess,
}) => {
  const { showAlert } = useDialog();
  const [transaction, setTransaction] = useState<TransaksiPembayaran>(initialTrx);
  const [secondsLeft, setSecondsLeft] = useState(900); // 15 minutes countdown
  const [isSimulating, setIsSimulating] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState('BCA Mobile');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Countdown timer
  useEffect(() => {
    if (transaction.status === 'SUCCESS') return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [transaction.status]);

  // Listen to Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Polling check status every 5 seconds if pending
  useEffect(() => {
    if (transaction.status === 'SUCCESS') return;

    const interval = setInterval(async () => {
      try {
        const res = await api.checkQrisStatus(transaction.noTransaksi);
        if (res.success && res.status === 'SUCCESS') {
          setTransaction(res.transaction);
          setStatusMessage('Pembayaran berhasil diverifikasi otomatis!');
        }
      } catch {
        // silent fail during polling
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [transaction.noTransaksi, transaction.status]);

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(mins).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSimulatePayment = async (issuerName: string) => {
    try {
      setIsSimulating(true);
      setSelectedIssuer(issuerName);
      const res = await api.simulateQrisPayment({
        transactionId: transaction.id,
        orderId: transaction.noTransaksi,
        simulatedIssuer: issuerName,
      });

      if (res.success) {
        setTransaction(res.transaction);
        setStatusMessage(`Pembayaran berhasil diselesaikan via ${issuerName}!`);
      }
    } catch (err: any) {
      showAlert(`Gagal simulasi QRIS: ${err.message}`, { type: 'danger' });
    } finally {
      setIsSimulating(false);
    }
  };

  const handleManualCheck = async () => {
    try {
      setIsCheckingStatus(true);
      const res = await api.checkQrisStatus(transaction.noTransaksi);
      if (res.success) {
        if (res.status === 'SUCCESS') {
          setTransaction(res.transaction);
          setStatusMessage('Pembayaran LUNAS!');
        } else {
          setStatusMessage('Status: Masih menunggu scan & pembayaran dari wali murid.');
        }
      }
    } catch (err: any) {
      showAlert(`Gagal cek status: ${err.message}`, { type: 'danger' });
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handlePrintQrisSlip = () => {
    window.print();
  };

  const nmid = transaction.qrisNmid || (transaction as any).qrisNmid || 'ID1020038491028';
  const merchantName = transaction.qrisMerchantName || (transaction as any).qrisMerchantName || 'SD MUHAMMADIYAH TERPADU PONOROGO';
  
  const rawTotalBayar = Number(transaction.totalBayar || 0);
  const biayaLayanan = Number(transaction.biayaLayanan || 1500);
  const finalTotalBayar = Number(
    transaction.totalBayarGateway || 
    (rawTotalBayar > 0 && rawTotalBayar % 1000 === 500 ? rawTotalBayar : (rawTotalBayar + biayaLayanan))
  );
  const nominalTagihan = finalTotalBayar > biayaLayanan ? (finalTotalBayar - biayaLayanan) : rawTotalBayar;

  // Try to get qrString from various possible camelCase or snake_case mappings
  const rawQrStr = (transaction as any).qrString || (transaction as any).qr_string || transaction.qrisString || '';
  
  // If empty, generate a mock QRIS string for visual testing
  const amtStr = finalTotalBayar.toString();
  const amtLenStr = amtStr.length.toString().padStart(2, '0');
  const qrString = rawQrStr || `00020101021226590016ID.CO.QRIS.WWW011893600010000000000051440014ID.LINKAJA.WWW0215000000000000000520458125303360540${amtLenStr}${amtStr}5802ID5929SD MUHAMMADIYAH TERPADU PO6008PONOROGO61056347162070703A016304`;
  
  const qrCodeUrl =
    transaction.qrisUrl || (transaction as any).qris_url ||
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrString)}`;

  return (
    <div
      id="qris-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 overflow-y-auto cursor-pointer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden my-6 animate-in fade-in zoom-in-95 cursor-default relative"
      >
        {/* Top Dialog Bar */}
        <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <div className="bg-rose-600 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-widest">
              QRIS
            </div>
            <span className="font-bold text-xs">Loket Kasir & Layar Pembayaran QRIS</span>
          </div>
          <button
            id="btn-close-qris-top"
            type="button"
            onClick={onClose}
            title="Tutup (Esc)"
            className="flex items-center gap-1 bg-white/15 hover:bg-rose-600 active:bg-rose-700 text-white px-2.5 py-1 rounded-md transition text-xs font-bold cursor-pointer"
          >
            <X className="w-4 h-4" />
            <span>Tutup</span>
          </button>
        </div>

        {/* Official Indonesian QRIS Display Layout */}
        <div className="p-6 text-slate-800 space-y-4" id="printable-qris">
          {/* Header Standard QRIS ASPI */}
          <div className="bg-rose-700 text-white rounded-xl p-3.5 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2.5">
              <div className="bg-white text-rose-700 font-black px-2.5 py-1 rounded-md text-sm tracking-wider">
                QRIS
              </div>
              <div>
                <p className="font-extrabold text-xs tracking-tight">
                  QRIS (Quick Response Code Indonesian Standard)
                </p>
                <p className="text-[10px] text-rose-200">
                  Satu QR Code untuk Semua Aplikasi Pembayaran Perbankan & e-Wallet
                </p>
              </div>
            </div>
            <div className="bg-white/15 px-2 py-1 rounded text-[10px] font-bold tracking-widest border border-white/20">
              GPN
            </div>
          </div>

          {/* Merchant Info Card */}
          <div className="text-center border-b pb-3 space-y-0.5">
            <h3 className="font-extrabold text-base text-slate-900 tracking-tight uppercase">
              {merchantName}
            </h3>
            <p className="text-xs text-slate-600">NMID: <span className="font-mono font-bold text-slate-900">{nmid}</span> • A01</p>
            <p className="text-[11px] text-sky-800 font-semibold">
              Siswa: {transaction.namaSiswa} ({transaction.nis}) • Kelas: {transaction.kelas}
            </p>
          </div>

          {/* QR Code Canvas Card */}
          <div className="flex flex-col items-center justify-center">
            {transaction.status === 'SUCCESS' ? (
              <div className="bg-emerald-50 border-2 border-emerald-500 rounded-2xl p-8 text-center w-full max-w-sm space-y-3 animate-in zoom-in-95">
                <div className="w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <div>
                  <h4 className="text-lg font-black text-emerald-950">PEMBAYARAN QRIS LUNAS!</h4>
                  <p className="text-xs text-emerald-800 font-semibold mt-1">
                    Transaksi telah terverifikasi dan berhasil diselesaikan.
                  </p>
                </div>

                <div className="bg-white p-3 rounded-lg border border-emerald-200 text-xs font-mono space-y-1 text-left">
                  <div className="flex justify-between">
                    <span className="text-slate-500">No. Transaksi:</span>
                    <span className="font-bold text-slate-900">{transaction.noTransaksi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">No. Kuitansi:</span>
                    <span className="font-bold text-slate-900">{transaction.noKuitansi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nominal Tagihan:</span>
                    <span className="font-bold text-slate-800">Rp {nominalTagihan.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Biaya Layanan:</span>
                    <span className="font-bold text-sky-700">+Rp {biayaLayanan.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between border-t border-emerald-200 pt-1">
                    <span className="text-slate-700 font-bold">Total Terbayar:</span>
                    <span className="font-black text-emerald-700">
                      Rp {finalTotalBayar.toLocaleString('id-ID')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Catatan:</span>
                    <span className="font-bold text-sky-700">{transaction.catatan}</span>
                  </div>
                </div>

                <div className="pt-2 flex gap-2">
                  <button
                    onClick={() => onPaymentSuccess(transaction)}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Cetak Kuitansi Resmi</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white border-2 border-slate-800 rounded-2xl p-4 shadow-sm text-center max-w-xs w-full">
                {/* Timer & Status Header */}
                <div className="flex items-center justify-between pb-2 border-b border-slate-100 text-xs">
                  <span className="flex items-center gap-1 text-slate-500 font-medium text-[11px]">
                    <Clock className="w-3.5 h-3.5 text-amber-500" />
                    <span>Berlaku:</span>
                  </span>
                  <span className="font-mono font-extrabold text-rose-600 bg-rose-50 px-2 py-0.5 rounded">
                    {formatTime(secondsLeft)}
                  </span>
                </div>

                {/* QR Code Render */}
                <div className="my-3 p-3 bg-white border border-slate-200 rounded-xl relative group">
                  <img
                    src={qrCodeUrl}
                    alt="QRIS Pembayaran Siswa"
                    className="w-56 h-56 mx-auto object-contain"
                  />
                  {/* Center QRIS logo */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-white px-2 py-1 rounded shadow-md border border-slate-300 font-black text-rose-700 text-[10px] tracking-wider">
                      QRIS
                    </div>
                  </div>
                </div>

                {/* Amount Breakdown Display */}
                <div className="border-t border-slate-100 pt-2 space-y-1">
                  <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                    <span>Nominal Tagihan:</span>
                    <span className="font-mono font-bold text-slate-700">Rp {nominalTagihan.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between text-[11px] text-sky-700 font-medium">
                    <span>Biaya Layanan & Bank (QRIS):</span>
                    <span className="font-mono font-bold">+Rp {biayaLayanan.toLocaleString('id-ID')}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-1 flex justify-between items-center">
                    <p className="text-[11px] text-slate-800 font-black">TOTAL PEMBAYARAN:</p>
                    <p className="text-xl font-black text-sky-900 font-mono tracking-tight">
                      Rp {finalTotalBayar.toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="mt-2.5 bg-amber-50 text-amber-900 text-[11px] py-1 px-2 rounded-lg border border-amber-200 flex items-center justify-center gap-1.5 font-medium">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  <span>Menunggu pembayaran dari wali murid...</span>
                </div>
              </div>
            )}
          </div>

          {/* Supported Issuers / Logo Showcase */}
          {transaction.status !== 'SUCCESS' && (
            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2 text-xs">
              <span className="font-bold text-slate-700 text-[11px] block text-center">
                Dapat discan dengan Aplikasi Mobile Banking & e-Wallet di Indonesia:
              </span>
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-semibold text-slate-600">
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">BCA Mobile</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">Livin' Mandiri</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">BRImo</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">BNI Mobile</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">Muamalat DIN</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">GoPay</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">OVO</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">Dana</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">ShopeePay</span>
                <span className="bg-white px-2 py-1 rounded border shadow-2xs">LinkAja</span>
              </div>
            </div>
          )}

          {/* Interactive Action & Simulator Controls (Print Hidden) */}
          {transaction.status !== 'SUCCESS' && (
            <div className="border-t pt-4 space-y-3 print:hidden">
              {/* Bottom Buttons */}
              <div className="flex flex-wrap items-center justify-between pt-2 gap-2">
                <div className="flex gap-2">
                  <button
                    disabled={isCheckingStatus}
                    onClick={handleManualCheck}
                    className="px-3 py-2 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isCheckingStatus ? 'animate-spin' : ''}`} />
                    <span>{isCheckingStatus ? 'Cek...' : 'Cek Status'}</span>
                  </button>
                  <button
                    onClick={handlePrintQrisSlip}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition cursor-pointer"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Cetak Slip Kasir</span>
                    <span className="sm:hidden">Cetak</span>
                  </button>
                  
                  {/* Buka Link Pembayaran for Web */}
                  {(transaction.paymentUrl || (transaction as any).qrisUrl) && (
                    <a
                      href={transaction.paymentUrl || (transaction as any).qrisUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Buka Link Pembayaran</span>
                      <span className="sm:hidden">Link</span>
                    </a>
                  )}
                </div>

                <button
                  id="btn-close-qris-bottom"
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 active:bg-slate-400 text-slate-800 text-xs font-bold rounded-lg transition cursor-pointer flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Tutup / Batal</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
